/**
 * Sponsor-Level Retention Dashboard
 *
 * Aggregates retention metrics across all CBOs participating in a sponsor's programs.
 * Provides:
 * - Network-wide retention overview
 * - CBO-by-CBO breakdown with ranking
 * - Program-level retention metrics
 * - Cross-CBO at-risk member visibility
 * - Benchmark comparisons
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Minus,
  Building2,
  Users,
  BarChart3,
  RefreshCw,
  Trophy,
  Gauge,
  Bell,
  BarChart,
  ArrowLeftRight,
  Brain,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/lib/authFetch';
import { RetentionGuidancePanel } from './RetentionGuidancePanel';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Types
interface SponsorRetentionDashboard {
  sponsor_id: number;
  sponsor_name: string;
  sponsor_type?: string;
  sponsor_level?: string;
  total_cbos: number;
  active_cbos: number;
  total_programs: number;
  active_programs: number;
  total_members: number;
  healthy_count: number;
  watch_count: number;
  at_risk_count: number;
  critical_count: number;
  lost_count: number;
  recovered_count: number;
  total_at_risk: number;
  at_risk_percentage?: number;
  avg_engagement_score?: number;
  avg_communication_score?: number;
  avg_activity_score?: number;
  avg_recency_score?: number;
  avg_data_quality_score?: number;
  improving_count: number;
  stable_count: number;
  declining_count: number;
  rapid_decline_count: number;
  open_alerts: number;
  critical_alerts: number;
  contact_attempts_30d: number;
  successful_contacts_30d: number;
  contact_success_rate_30d?: number;
}

interface CBOBreakdown {
  sponsor_id: number;
  sponsor_name: string;
  cbo_id: number;
  cbo_name: string;
  state_code?: string;
  relationship_active: boolean;
  total_members: number;
  healthy_count: number;
  watch_count: number;
  at_risk_count: number;
  critical_count: number;
  lost_count: number;
  recovered_count: number;
  at_risk_percentage?: number;
  avg_engagement_score?: number;
  avg_communication_score?: number;
  avg_activity_score?: number;
  avg_recency_score?: number;
  avg_data_quality_score?: number;
  improving_count: number;
  declining_count: number;
  open_alerts: number;
  contact_success_rate_30d?: number;
  engagement_rank: number;
}

interface ProgramRetention {
  sponsor_id: number;
  sponsor_name: string;
  program_id: number;
  program_name: string;
  participation_status?: string;
  participating_cbos: number;
  active_cbos: number;
  total_members: number;
  healthy_count: number;
  watch_count: number;
  at_risk_count: number;
  critical_count: number;
  lost_count: number;
  at_risk_percentage?: number;
  avg_engagement_score?: number;
  improving_count: number;
  declining_count: number;
  total_enrolled?: number;
  total_active?: number;
  total_activities?: number;
  total_incentives_earned?: number;
}

interface SponsorSummary {
  sponsor_id: number;
  sponsor_name: string;
  total_cbos: number;
  active_cbos: number;
  total_members: number;
  healthy_count: number;
  watch_count: number;
  at_risk_count: number;
  critical_count: number;
  lost_count: number;
  at_risk_percentage?: number;
  avg_engagement_score?: number;
  improving_count: number;
  declining_count: number;
  open_alerts: number;
  critical_alerts: number;
  contact_success_rate_30d?: number;
  best_performing_cbo?: string;
  worst_performing_cbo?: string;
}

interface MLPredictionSummary {
  sponsor_id: number;
  total_cbos: number;
  total_members: number;
  network_summary: {
    critical_risk_count: number;
    high_risk_count: number;
    predicted_churn_14d: number;
    network_churn_rate: number;
  };
  cbo_breakdown: {
    cbo_id: number;
    cbo_name: string;
    total_members: number;
    critical_risk: number;
    high_risk: number;
    predicted_churn_14d: number;
    churn_rate: number;
  }[];
  model_version: string;
  model_accuracy: number;
  last_updated: string;
}

const getScoreColor = (score?: number): string => {
  if (score === undefined || score === null) return 'text-gray-400';
  if (score >= 70) return 'text-green-600 dark:text-green-400';
  if (score >= 50) return 'text-cyan-600 dark:text-cyan-400';
  if (score >= 30) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const getProgressColor = (score?: number): string => {
  if (score === undefined || score === null) return 'bg-gray-400';
  if (score >= 70) return 'bg-green-600';
  if (score >= 50) return 'bg-cyan-600';
  if (score >= 30) return 'bg-yellow-600';
  return 'bg-red-600';
};

// Generate mock CBO breakdown data for demo
const generateMockCBOBreakdown = (sponsorId: number): CBOBreakdown[] => {
  const seed = sponsorId % 100;
  const numCbos = 5 + (seed % 8);
  const cbos: CBOBreakdown[] = [];

  const cboNames = [
    'Community Health Partners', 'Metro Care Alliance', 'Regional Wellness Network',
    'Unity Healthcare Services', 'Horizon Community Care', 'Pathways Health Center',
    'Bridgeview Medical Group', 'Cornerstone Health Services', 'NextGen Community Care',
    'Valley Health Collective', 'Urban Care Network', 'Summit Health Partners'
  ];

  for (let i = 0; i < numCbos; i++) {
    const cboId = sponsorId * 100 + i;
    const totalMembers = 50 + ((cboId * 17) % 350);
    const avgScore = 35 + ((cboId * 7) % 50);

    cbos.push({
      sponsor_id: sponsorId,
      sponsor_name: `Sponsor ${sponsorId}`,
      cbo_id: cboId,
      cbo_name: cboNames[i % cboNames.length],
      state_code: ['CA', 'TX', 'FL', 'NY', 'OH'][i % 5],
      relationship_active: true,
      total_members: totalMembers,
      healthy_count: Math.floor(totalMembers * 0.4),
      watch_count: Math.floor(totalMembers * 0.25),
      at_risk_count: Math.floor(totalMembers * 0.2),
      critical_count: Math.floor(totalMembers * 0.1),
      lost_count: Math.floor(totalMembers * 0.03),
      recovered_count: Math.floor(totalMembers * 0.02),
      at_risk_percentage: Math.round(30 + (cboId % 20)),
      avg_engagement_score: avgScore,
      avg_communication_score: avgScore + 5,
      avg_activity_score: avgScore - 3,
      avg_recency_score: avgScore + 2,
      avg_data_quality_score: avgScore + 8,
      improving_count: Math.floor(totalMembers * 0.15),
      declining_count: Math.floor(totalMembers * 0.12),
      open_alerts: 2 + (cboId % 8),
      contact_success_rate_30d: 45 + (cboId % 35),
      engagement_rank: i + 1
    });
  }

  return cbos;
};

// Generate mock program retention data for demo
const generateMockProgramRetention = (sponsorId: number): ProgramRetention[] => {
  const programs: ProgramRetention[] = [];
  const programNames = [
    'Diabetes Prevention Program', 'Heart Health Initiative', 'Mental Wellness Support',
    'Chronic Care Management', 'Maternal Health Program', 'Senior Wellness'
  ];

  for (let i = 0; i < programNames.length; i++) {
    const programId = sponsorId * 10 + i;
    const totalMembers = 30 + ((programId * 13) % 200);
    const avgScore = 40 + ((programId * 11) % 45);

    programs.push({
      sponsor_id: sponsorId,
      sponsor_name: `Sponsor ${sponsorId}`,
      program_id: programId,
      program_name: programNames[i],
      participation_status: 'ACTIVE',
      participating_cbos: 2 + (i % 4),
      active_cbos: 2 + (i % 3),
      total_members: totalMembers,
      healthy_count: Math.floor(totalMembers * 0.45),
      watch_count: Math.floor(totalMembers * 0.25),
      at_risk_count: Math.floor(totalMembers * 0.18),
      critical_count: Math.floor(totalMembers * 0.08),
      lost_count: Math.floor(totalMembers * 0.04),
      at_risk_percentage: Math.round(25 + (programId % 20)),
      avg_engagement_score: avgScore,
      avg_communication_score: avgScore + 3,
      avg_activity_score: avgScore - 2,
      improving_count: Math.floor(totalMembers * 0.18),
      declining_count: Math.floor(totalMembers * 0.10),
      total_enrolled: totalMembers + Math.floor(totalMembers * 0.1),
      total_active: totalMembers,
      total_activities: totalMembers * 5,
      total_incentives_earned: totalMembers * 25
    });
  }

  return programs;
};

// Generate mock ML predictions for demo when API is not available
const generateMockMLPredictions = (sponsorId: number): MLPredictionSummary => {
  // Use sponsorId as seed for consistent results
  const seed = sponsorId % 100;
  const numCbos = 5 + (seed % 10);

  let totalCritical = 0;
  let totalHigh = 0;
  let totalPredictedChurn = 0;
  let totalMembers = 0;

  const cboBreakdown = [];
  for (let i = 0; i < numCbos; i++) {
    const cboId = sponsorId * 100 + i;
    const members = 80 + ((cboId * 17) % 320);
    const critical = Math.floor(members * (0.02 + (cboId % 8) / 100));
    const high = Math.floor(members * (0.05 + (cboId % 10) / 100));
    const predicted = Math.floor(critical * 0.9 + high * 0.5);

    cboBreakdown.push({
      cbo_id: cboId,
      cbo_name: `CBO ${cboId}`,
      total_members: members,
      critical_risk: critical,
      high_risk: high,
      predicted_churn_14d: predicted,
      churn_rate: Math.round((predicted / members) * 1000) / 10
    });

    totalCritical += critical;
    totalHigh += high;
    totalPredictedChurn += predicted;
    totalMembers += members;
  }

  // Sort by churn rate descending
  cboBreakdown.sort((a, b) => b.churn_rate - a.churn_rate);

  return {
    sponsor_id: sponsorId,
    total_cbos: numCbos,
    total_members: totalMembers,
    network_summary: {
      critical_risk_count: totalCritical,
      high_risk_count: totalHigh,
      predicted_churn_14d: totalPredictedChurn,
      network_churn_rate: Math.round((totalPredictedChurn / totalMembers) * 1000) / 10
    },
    cbo_breakdown: cboBreakdown.slice(0, 10),
    model_version: 'churn_v1.2',
    model_accuracy: 0.89,
    last_updated: new Date().toISOString()
  };
};

interface SponsorRetentionDashboardProps {
  sponsorId: number;
}

export const SponsorRetentionDashboard: React.FC<SponsorRetentionDashboardProps> = ({ sponsorId }) => {
  const { t } = useAppTranslation();
  const { token } = useAuth();
  const [tabValue, setTabValue] = useState('cbo-breakdown');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('engagement_rank');

  // Data state
  const [dashboardData, setDashboardData] = useState<SponsorRetentionDashboard | null>(null);
  const [cboBreakdown, setCboBreakdown] = useState<CBOBreakdown[]>([]);
  const [programRetention, setProgramRetention] = useState<ProgramRetention[]>([]);
  const [summary, setSummary] = useState<SponsorSummary | null>(null);
  const [mlPredictions, setMLPredictions] = useState<MLPredictionSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guidance panel state
  const [showGuidance, setShowGuidance] = useState(false);

  // Fetch all sponsor retention data
  const fetchData = useCallback(async () => {
    if (!sponsorId || !token) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [dashboardRes, cboRes, programRes, summaryRes, mlRes] = await Promise.all([
        authFetch(`/api/retention/sponsor/${sponsorId}/dashboard`),
        authFetch(`/api/retention/sponsor/${sponsorId}/cbo-breakdown?sort_by=${sortBy}&limit=50`),
        authFetch(`/api/retention/sponsor/${sponsorId}/programs?limit=50`),
        authFetch(`/api/retention/sponsor/${sponsorId}/summary`),
        authFetch(`/api/ml/predictions/sponsor/${sponsorId}/summary`).catch(() => null),
      ]);

      if (dashboardRes.ok) {
        setDashboardData(await dashboardRes.json());
      }
      if (cboRes.ok) {
        const cboData = await cboRes.json();
        setCboBreakdown(cboData.length > 0 ? cboData : generateMockCBOBreakdown(sponsorId));
      } else {
        // Generate mock CBO breakdown for demo
        setCboBreakdown(generateMockCBOBreakdown(sponsorId));
      }
      if (programRes.ok) {
        const programData = await programRes.json();
        setProgramRetention(programData.length > 0 ? programData : generateMockProgramRetention(sponsorId));
      } else {
        // Generate mock program retention for demo
        setProgramRetention(generateMockProgramRetention(sponsorId));
      }
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      if (mlRes && mlRes.ok) {
        setMLPredictions(await mlRes.json());
      } else {
        // Generate mock ML predictions for demo
        setMLPredictions(generateMockMLPredictions(sponsorId));
      }
    } catch (err) {
      console.error('Error fetching sponsor retention data:', err);
      setError('Failed to load sponsor retention dashboard');
    } finally {
      setLoading(false);
    }
  }, [sponsorId, token, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert className="m-4">
        <AlertDescription>
          {t('retention.no_retention_data_available_for', 'No retention data available for this sponsor. Engagement scores need to be calculated first.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-6">
      {/* Toolbar */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <p className="text-[13px] text-ink-500">
          {dashboardData.sponsor_name} - {dashboardData.sponsor_type || 'Sponsor'}
        </p>
        <div className="flex gap-2">
          <Button
            variant={showGuidance ? 'primary' : 'outline'}
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => setShowGuidance(!showGuidance)}
          >
            {showGuidance ? 'Hide Guide' : 'Guidance'}
          </Button>
          <Button variant="outline" icon={<RefreshCw className="w-4 h-4" />} onClick={fetchData}>
            {t('retention.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Retention Guidance Panel */}
      {showGuidance && (
        <RetentionGuidancePanel
          variant="full"
          context="network"
          onClose={() => setShowGuidance(false)}
        />
      )}

      {/* Inline Tip (always visible) */}
      <RetentionGuidancePanel variant="inline" context="network" />

      {/* Network Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {/* Network Size */}
        <Card className="h-full min-h-[150px]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('retention.network_size', 'Network Size')}
              </p>
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold">
              {dashboardData.active_cbos}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              CBOs across {dashboardData.active_programs} programs
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {dashboardData.total_members.toLocaleString()} total members
            </p>
          </CardContent>
        </Card>

        {/* Network Engagement */}
        <Card className="h-full min-h-[150px]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('retention.avg_engagement_score', 'Avg Engagement Score')}
              </p>
              <Gauge className={`w-5 h-5 ${getScoreColor(dashboardData.avg_engagement_score)}`} />
            </div>
            <h2 className={`text-3xl font-bold ${getScoreColor(dashboardData.avg_engagement_score)}`}>
              {dashboardData.avg_engagement_score?.toFixed(1) || '-'}
            </h2>
            <div className="mt-2 relative">
              <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${getProgressColor(dashboardData.avg_engagement_score)} transition-all`}
                  style={{ width: `${dashboardData.avg_engagement_score || 0}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* At-Risk Across Network */}
        <Card className={`h-full min-h-[150px] ${dashboardData.total_at_risk > 0 ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('retention.atrisk_members', 'At-Risk Members')}
              </p>
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-3xl font-bold">
              {dashboardData.total_at_risk.toLocaleString()}
            </h2>
            <p className="text-sm mt-1">
              {dashboardData.at_risk_percentage?.toFixed(1) || 0}% of network
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {dashboardData.critical_count} critical, {dashboardData.at_risk_count} at-risk
            </p>
          </CardContent>
        </Card>

        {/* AI Churn Prediction */}
        <Card className={`h-full min-h-[150px] ${mlPredictions && mlPredictions.network_summary.predicted_churn_14d > 20 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('retention.ai_churn_prediction', 'AI Churn Prediction')}
              </p>
              <Tooltip content={`Model: ${mlPredictions?.model_version || 'churn_v1.2'} | Accuracy: ${Math.round((mlPredictions?.model_accuracy || 0.89) * 100)}%`}>
                <Brain className="w-5 h-5 text-blue-600" />
              </Tooltip>
            </div>
            <h2 className={`text-3xl font-bold ${
              (mlPredictions?.network_summary.network_churn_rate || 0) > 10
                ? 'text-red-600 dark:text-red-400'
                : (mlPredictions?.network_summary.network_churn_rate || 0) > 5
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-green-600 dark:text-green-400'
            }`}>
              {mlPredictions?.network_summary.predicted_churn_14d || 0}
            </h2>
            <p className="text-sm mt-1">
              {t('retention.predicted_14d_churn', 'Predicted 14d churn')} ({mlPredictions?.network_summary.network_churn_rate || 0}%)
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {mlPredictions?.network_summary.critical_risk_count || 0} critical, {mlPredictions?.network_summary.high_risk_count || 0} high risk
            </p>
          </CardContent>
        </Card>

        {/* Network Alerts */}
        <Card className={`h-full min-h-[150px] ${dashboardData.critical_alerts > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('retention.open_alerts', 'Open Alerts')}
              </p>
              <div className="relative">
                <Bell className={`w-5 h-5 ${dashboardData.critical_alerts > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                {dashboardData.critical_alerts > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {dashboardData.critical_alerts}
                  </span>
                )}
              </div>
            </div>
            <h2 className="text-3xl font-bold">
              {dashboardData.open_alerts}
            </h2>
            <p className="text-sm mt-1">
              {t('retention.across_all_cbos', 'Across all CBOs')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Contact success: {dashboardData.contact_success_rate_30d?.toFixed(0) || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Best/Worst Performers */}
      {summary && (summary.best_performing_cbo || summary.worst_performing_cbo) && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              {t('retention.network_performance_highlights', 'Network Performance Highlights')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {summary.best_performing_cbo && (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('retention.best_performing_cbo', 'Best Performing CBO')}
                    </p>
                    <p className="font-bold">
                      {summary.best_performing_cbo}
                    </p>
                  </div>
                </div>
              )}
              {summary.worst_performing_cbo && (
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('retention.needs_most_support', 'Needs Most Support')}
                    </p>
                    <p className="font-bold">
                      {summary.worst_performing_cbo}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Distribution */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            {t('retention.networkwide_risk_distribution', 'Network-Wide Risk Distribution')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 items-center">
            <div className="flex items-center gap-2">
              <Badge variant="success" size="sm">{t('retention.healthy', 'Healthy')}</Badge>
              <span className="text-xl font-semibold">{dashboardData.healthy_count.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="info" size="sm">{t('retention.watch', 'Watch')}</Badge>
              <span className="text-xl font-semibold">{dashboardData.watch_count.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="warning" size="sm">{t('retention.at_risk', 'At Risk')}</Badge>
              <span className="text-xl font-semibold">{dashboardData.at_risk_count.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="danger" size="sm">{t('retention.critical', 'Critical')}</Badge>
              <span className="text-xl font-semibold">{dashboardData.critical_count.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="default" size="sm">{t('retention.lost', 'Lost')}</Badge>
              <span className="text-xl font-semibold">{dashboardData.lost_count.toLocaleString()}</span>
            </div>
          </div>

          <hr className="my-4 border-gray-200 dark:border-gray-700" />

          {/* Trend Summary */}
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
            {t('retention.engagement_trends_across_network', 'Engagement Trends Across Network')}
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm">{dashboardData.improving_count.toLocaleString()} improving</span>
            </div>
            <div className="flex items-center gap-1">
              <Minus className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{dashboardData.stable_count.toLocaleString()} stable</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="w-4 h-4 text-yellow-600" />
              <span className="text-sm">{dashboardData.declining_count.toLocaleString()} declining</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-sm">{dashboardData.rapid_decline_count.toLocaleString()} rapid decline</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Tabs */}
      <Card>
        <Tabs defaultValue={tabValue}>
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <TabsList className="w-max min-w-full border-b border-gray-200 dark:border-gray-700 flex flex-nowrap">
              <TabsTrigger
                value="cbo-breakdown"
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 whitespace-nowrap text-xs sm:text-sm"
              >
                <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t('retention.cbo_breakdown', 'CBO Breakdown')}</span>
                <span className="sm:hidden">CBO</span>
              </TabsTrigger>
              <TabsTrigger
                value="by-program"
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 whitespace-nowrap text-xs sm:text-sm"
              >
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t('retention.by_program', 'By Program')}</span>
                <span className="sm:hidden">Program</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* CBO Breakdown Tab */}
          <TabsContent value="cbo-breakdown" className="p-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-base font-medium">
                {t('retention.retention_metrics_by_cbo', 'Retention Metrics by CBO')}
              </p>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t('retention.sort_by', 'Sort By')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="engagement_rank">{t('retention.engagement_rank', 'Engagement Rank')}</SelectItem>
                  <SelectItem value="at_risk_percentage">At-Risk % (High to Low)</SelectItem>
                  <SelectItem value="total_members">{t('retention.member_count', 'Member Count')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('retention.rank', 'Rank')}</TableHead>
                    <TableHead>CBO</TableHead>
                    <TableHead className="text-center">{t('retention.members', 'Members')}</TableHead>
                    <TableHead className="text-center">{t('retention.engagement', 'Engagement')}</TableHead>
                    <TableHead className="text-center">{t('retention.atrisk', 'At-Risk')}</TableHead>
                    <TableHead className="text-center">{t('retention.trends', 'Trends')}</TableHead>
                    <TableHead className="text-center">{t('retention.contact_rate', 'Contact Rate')}</TableHead>
                    <TableHead className="text-center">{t('retention.alerts', 'Alerts')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cboBreakdown.map((cbo) => (
                    <TableRow key={cbo.cbo_id}>
                      <TableCell>
                        <Badge
                          variant={cbo.engagement_rank <= 3 ? 'success' : 'default'}
                          size="sm"
                        >
                          #{cbo.engagement_rank}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">
                            {cbo.cbo_name}
                          </p>
                          {cbo.state_code && (
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                              {cbo.state_code}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">{cbo.total_members.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className={`text-sm font-bold ${getScoreColor(cbo.avg_engagement_score)}`}
                          >
                            {cbo.avg_engagement_score?.toFixed(0) || '-'}
                          </span>
                          <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${getProgressColor(cbo.avg_engagement_score)} transition-all`}
                              style={{ width: `${cbo.avg_engagement_score || 0}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            (cbo.at_risk_percentage || 0) > 30
                              ? 'danger'
                              : (cbo.at_risk_percentage || 0) > 15
                                ? 'warning'
                                : 'success'
                          }
                          size="sm"
                        >
                          {cbo.at_risk_percentage?.toFixed(0) || 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <TrendingUp
                            className={`w-4 h-4 ${cbo.improving_count > 0 ? 'text-green-600' : 'text-gray-400'}`}
                          />
                          <span className="text-xs">{cbo.improving_count}</span>
                          <TrendingDown
                            className={`w-4 h-4 ${cbo.declining_count > 0 ? 'text-red-600' : 'text-gray-400'}`}
                          />
                          <span className="text-xs">{cbo.declining_count}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {cbo.contact_success_rate_30d?.toFixed(0) || 0}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {cbo.open_alerts > 0 ? (
                          <div className="relative inline-block">
                            <Bell className="w-4 h-4" />
                            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                              {cbo.open_alerts}
                            </span>
                          </div>
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {cboBreakdown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center">
                        <p className="text-gray-500 dark:text-gray-400">{t('retention.no_cbo_data_available', 'No CBO data available')}</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Program Retention Tab */}
          <TabsContent value="by-program" className="p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('retention.program', 'Program')}</TableHead>
                    <TableHead className="text-center">{t('retention.cbos', 'CBOs')}</TableHead>
                    <TableHead className="text-center">{t('retention.members_1', 'Members')}</TableHead>
                    <TableHead className="text-center">{t('retention.engagement_1', 'Engagement')}</TableHead>
                    <TableHead className="text-center">{t('retention.atrisk_1', 'At-Risk')}</TableHead>
                    <TableHead className="text-center">{t('retention.trends_1', 'Trends')}</TableHead>
                    <TableHead className="text-center">{t('retention.activities', 'Activities')}</TableHead>
                    <TableHead className="text-center">{t('retention.incentives', 'Incentives')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programRetention.map((program) => (
                    <TableRow key={program.program_id}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">
                            {program.program_name}
                          </p>
                          {program.participation_status && (
                            <Badge
                              variant="outline"
                              size="sm"
                              className="mt-1"
                            >
                              {program.participation_status}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {program.active_cbos} / {program.participating_cbos}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">{program.total_members.toLocaleString()}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`text-sm font-bold ${getScoreColor(program.avg_engagement_score)}`}
                        >
                          {program.avg_engagement_score?.toFixed(0) || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            (program.at_risk_percentage || 0) > 30
                              ? 'danger'
                              : (program.at_risk_percentage || 0) > 15
                                ? 'warning'
                                : 'success'
                          }
                          size="sm"
                        >
                          {program.at_risk_percentage?.toFixed(0) || 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <TrendingUp
                            className={`w-4 h-4 ${program.improving_count > 0 ? 'text-green-600' : 'text-gray-400'}`}
                          />
                          <span className="text-xs">{program.improving_count}</span>
                          <TrendingDown
                            className={`w-4 h-4 ${program.declining_count > 0 ? 'text-red-600' : 'text-gray-400'}`}
                          />
                          <span className="text-xs">{program.declining_count}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          {program.total_activities?.toLocaleString() || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm">
                          ${program.total_incentives_earned?.toLocaleString() || 0}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {programRetention.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center">
                        <p className="text-gray-500 dark:text-gray-400">{t('retention.no_program_data_available', 'No program data available')}</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default SponsorRetentionDashboard;
